import { ComponentProps } from 'preact';
import { TableWebElementBase } from './TableWebElementBase';
import { Table } from 'oj-c/table';
import { By, error, WebElement } from 'selenium-webdriver';
import { SlotProxy, slotProxy } from '@oracle/oraclejet-webdriver';
import { executeWithModules } from '@oracle/oraclejet-webdriver/lib/oj-module-proxy';
import { findTableView } from '@oracle/oraclejet-preact/UNSAFE_TableView/testing';
import { byCss } from '@oracle/oraclejet-testing/UNSAFE_Locators';
import { getTestElement } from '../../../webdriver/PRIVATE_preact-webdriver';

type TableElement<K extends string | number, D, C extends string> = ComponentProps<
  typeof Table<K, D, C>
> &
  HTMLElement;

/**
 * The component WebElement for [oj-c-table](../../jsdocs/oj-c.Table.html).
 * Do not instantiate this class directly, instead, use
 * [findTable](../functions/findTable.html).
 */
export class TableWebElement extends TableWebElementBase {
  // Put overrides here
  /**
   * Sets the value of "selected" property.
   * If row/column are both unspecified selected for both will be empty.
   * If no object is set on row/column, selected for that axis will be empty.
   * If 'all' is unset for row/column object only 'keys' will be considered, if no 'keys' are specified selected for that axis will be empty.
   * See the Help documentation for more information.
   * @param selected The value to set for "selected"
   * @typeparam K Type of keys
   */
  async changeSelected<K extends string | number, C extends string>(selected: {
    row:
      | { all: true; keys?: never; deletedKeys: Array<K> }
      | { all: false; keys: Array<K>; deletedKeys?: never };
    column:
      | { all: true; keys?: never; deletedKeys: Array<C> }
      | { all: false; keys: Array<C>; deletedKeys?: never };
  }) {
    await this.whenReady();
    return executeWithModules<void>(
      this.getDriver(),
      ['KeySet'],
      [this, selected],
      ({ KeySetImpl, AllKeySetImpl }, ele, selected) => {
        ele.selected = {
          row: selected.row?.all
            ? new AllKeySetImpl().delete(selected.row.deletedKeys)
            : new KeySetImpl(selected.row?.keys),
          column: selected.column?.all
            ? new AllKeySetImpl().delete(selected.column.deletedKeys)
            : new KeySetImpl(selected.column?.keys)
        };
      }
    );
  }

  /**
   * Gets the value of "selected" property.
   * @typeparam K Type of keys
   * @return The value of "selected" property.
   */
  async getSelected<K extends string | number, C extends string>(): Promise<{
    row:
      | { all: true; keys?: never; deletedKeys: Array<K> }
      | { all: false; keys: Array<K>; deletedKeys?: never };
    column:
      | { all: true; keys?: never; deletedKeys: Array<C> }
      | { all: false; keys: Array<C>; deletedKeys?: never };
  }> {
    await this.whenReady();
    const selected: {
      row:
        | { all: true; keys?: never; deletedKeys: Array<K> }
        | { all: false; keys: Array<K>; deletedKeys?: never };
      column:
        | { all: true; keys?: never; deletedKeys: Array<C> }
        | { all: false; keys: Array<C>; deletedKeys?: never };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } = await this.getDriver().executeScript((ele: TableElement<K, any, C>) => {
      const row = ele.selected?.row?.keys.all
        ? { all: true, deletedKeys: Array.from(ele.selected.row.keys.deletedKeys.values()) }
        : { all: false, keys: Array.from(ele.selected?.row?.keys.keys?.values() || []) };
      const column = ele.selected?.column?.keys.all
        ? { all: true, deletedKeys: Array.from(ele.selected.column.keys.deletedKeys.values()) }
        : { all: false, keys: Array.from(ele.selected?.column?.keys.keys?.values() || []) };
      return { row, column };
    }, this);
    return selected;
  }

  /**
   * Set a given row active. Triggers a ojRowAction Event.
   * @param itemLocator.rowKey The rowKey associated with the row.
   */
  async doRowAction<K extends string | number>(itemLocator: { rowKey: K }): Promise<void> {
    await this.whenReady();
    return findTableView(byCss('* > div > div'), getTestElement(this)).doRowAction(
      itemLocator.rowKey
    );
  }

  /**
   * Retrieve a SlotProxy which represents a cell.
   * Cell content is only retrievable if there is a cell template for the cell.
   * Throws if there is no matching cell or if there is not cell template for the cell.
   *
   * @param itemLocator.rowKey The rowKey associated with the cell
   * @param itemLocator.columnKey The columnKey associated with the cell
   */
  async findCell<K extends string | number, C extends string>(itemLocator: {
    rowKey: K;
    columnKey: C;
  }): Promise<SlotProxy> {
    await this.whenReady();
    const el = await this.getDriver().executeScript<WebElement | null>(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (element: TableElement<K, any, C>, columnKey: C, rowKey: K) => {
        const column = element.columns && element.columns[columnKey];
        if (column != null) {
          const hasDefaultCellTemplate = element.firstElementChild?.getAttribute('data-oj-ct');
          if (column['template'] == null && !hasDefaultCellTemplate) {
            return null;
          }
          const row = element.querySelector(`[data-oj-key="${rowKey}"]`);
          if (row) {
            return row.querySelector(`[data-oj-column-key="${columnKey}"]`);
          }
        }
        return null;
      },
      this,
      itemLocator.columnKey,
      itemLocator.rowKey
    );

    if (el) {
      return slotProxy(el, this);
    } else {
      throw Error(`No corresponding item found for the itemLocator`);
    }
  }

  /**
   * Retrieve a SlotProxy which represents a header cell.
   * Header content is only retrievable if there is a header template for the cell.
   * Throws if there is no matching header or if there is not header template for the cell.
   *
   * @param itemLocator.columnKey The columnKey associated with the header cell
   */
  async findHeader<C extends string>(itemLocator: { columnKey: C }): Promise<SlotProxy> {
    await this.whenReady();
    const el = await this.getDriver().executeScript<WebElement | null>(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (element: TableElement<string, any, C>, columnKey: C) => {
        const column = element.columns && element.columns[columnKey];
        if (column != null) {
          const hasDefaultHeaderTemplate = element.firstElementChild?.getAttribute('data-oj-ht');
          if (column['headerTemplate'] == null && !hasDefaultHeaderTemplate) {
            return null;
          }
          return element.querySelector(
            `[data-oj-cell-type="header"][data-oj-column-key="${columnKey}"]`
          );
        }
        return null;
      },
      this,
      itemLocator.columnKey
    );

    if (el) {
      return slotProxy(el, this);
    } else {
      throw Error(`No corresponding item found for the itemLocator`);
    }
  }

  /**
   * Retrieve a SlotProxy which represents a footer cell.
   * Footer content is only retrievable if there is a footer template for the cell.
   * Throws if there is no matching footer or if there is not footer template for the cell.
   *
   * @param itemLocator.columnKey The columnKey associated with the footer cell
   */
  async findFooter<C extends string>(itemLocator: { columnKey: C }): Promise<SlotProxy> {
    await this.whenReady();
    const el = await this.getDriver().executeScript<WebElement | null>(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (element: TableElement<string | number, any, C>, columnKey: C) => {
        const column = element.columns && element.columns[columnKey];
        if (column != null) {
          const hasDefaultFooterTemplate = element.firstElementChild?.getAttribute('data-oj-ft');
          if (column['footerTemplate'] == null && !hasDefaultFooterTemplate) {
            return null;
          }
          return element.querySelector(
            `[data-oj-cell-type="footer"][data-oj-column-key="${columnKey}"]`
          );
        }
        return null;
      },
      this,
      itemLocator.columnKey
    );

    if (el) {
      return slotProxy(el, this);
    } else {
      throw Error(`No corresponding item found for the itemLocator`);
    }
  }

  /**
   * Retrieve a SlotProxy which represents no data content.
   */
  async findNoData(): Promise<SlotProxy> {
    await this.whenReady();
    const el = await this.getDriver().executeScript<WebElement | null>(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (element: TableElement<string | number, any, string>) => {
        if (!element.firstElementChild?.getAttribute('data-oj-ndt')) {
          return null;
        }
        return element.querySelector('[data-oj-cell-type="noData"]');
      },
      this
    );
    if (el) {
      return slotProxy(el, this);
    } else {
      throw Error(`No corresponding item found for the itemLocator`);
    }
  }
}
